import type { SystemPackageResourceApi } from '@ingenico/api-sdk-device-management-private';
import type { UpdateSystemPackageVersionByIdV2MutationRequestApi } from '@ingenico/api-sdk-device-management-private-v2';
import type { PaginationApi } from '@ingenico/api-sdk-device-management-public';
import type { VersionStatus } from '../../pages/system-package-details-page/SystemPackageDetailsPage.model';
import type { ApplicationArtifactType } from './application.model';
import type { VersionPublicationStatus } from './applicationVersion.model';
import type { ArtifactComponentTypeEnum, ArtifactResource, ArtifactTypeEnum } from './artifact.model';
import type { AssetResourceReadV2ApiFront } from './asset.model';
import type { ComponentVersionDependencyMetadataFront } from './componentVersionDependencyMetadata.model';
import type { MappedCreateDownloadRules } from './downloadRules.model';
import type { PaginatedListResponseFront } from './pagination.model';

export interface PaginatedSystemPackageListV2 {
  readonly pagination: PaginationApi;
  readonly data: SystemPackageResourceFront[];
}

export interface PaginatedSystemPackageListV1 {
  readonly pagination: PaginationApi;
  readonly data: SystemPackageResourceApi[];
}

//----------------System Packages Version---------------------

export interface SystemPackageResourceFront {
  readonly compatibleDeviceFamilyNames?: string[];
  readonly creationDate?: Date | string;
  readonly id?: string;
  readonly longDescription?: string;
  readonly name?: string;
  readonly publisherId?: string;
  readonly shortDescription?: string;
  readonly status?: VersionPublicationStatus;
  readonly assets: AssetResourceReadV2ApiFront[];
  readonly systemPackageVersionIds: string[];
}

export interface SystemPackageVersionResourceFront {
  readonly artifactIds?: string[];
  readonly compatibleOsVersionIds?: string[];
  readonly creationDate?: Date | string;
  readonly id?: string;
  readonly longDescription?: string;
  readonly publisherId?: string;
  readonly shortDescription?: string;
  readonly status?: VersionPublicationStatus;
  readonly systemPackageId?: string;
  readonly templateIds?: string[];
  readonly versionNumber?: string;
}

export type SystemPackagesVersionResourceFront = SystemPackageVersionResourceFront & {
  readonly compatibleOsVersions: ComponentVersionDependencyMetadataFront[];
  readonly downloadRules?: MappedCreateDownloadRules[];
};

//------------------Put System Package Version----------------

export interface PutSystemPackageVersion {
  readonly systemPackagesVersionId: string;
  readonly body: UpdateSystemPackageVersionByIdV2MutationRequestApi;
}

//------------------System Packages Artifacts-----------------

export type SystemPackageResourceArtifact = Omit<ArtifactResource, 'type'> & {
  readonly artifactType: ArtifactTypeEnum;
};

export type SystemPackageArtifactListResponseFront = PaginatedListResponseFront & {
  readonly artifacts: SystemPackageResourceArtifact[];
};

export interface SystemPackageResource {
  readonly compatibleDeviceFamilyNames?: string[];
  readonly logo?: string;
  readonly longDescription?: string;
  readonly shortDescription?: string;
  readonly name?: string;
  readonly status?: string;
}

export interface SystemPackageVersionTemplateAssociations {
  readonly artifactId: string;
  readonly targetLocation: string;
}

export enum SystemPackageArtifactType {
  Template = 'TEMPLATE',
  SwPackage = 'SW_PACKAGE',
  DataFile = 'DATA_FILE',
}

export interface GenericArtifact {
  readonly artifactName: string;
  readonly checksum: string;
  readonly type: ApplicationArtifactType | SystemPackageArtifactType;
  readonly file: Blob;
  readonly id: string;
  readonly componentType: ArtifactComponentTypeEnum;
}

export interface MappedSystemPackageVersionTemplate {
  readonly artifactId: string;
  readonly targetLocation?: string;
}

export interface MappedSystemPackageVersionResourceReadApi {
  readonly version: string;
  readonly versionNumber?: string;
  readonly creationDate: Date | string;
  readonly status: VersionStatus;
  readonly id: string;
  readonly longDescription?: string;
  readonly shortDescription?: string;
  readonly compatibleOsVersionIds?: string[];
  readonly artifactIds?: string[];
  readonly templates?: MappedSystemPackageVersionTemplate[];
  readonly systemPackageId?: string;
  readonly downloadRules?: MappedCreateDownloadRules[];
}

export interface MappedSystemPackageVersionResourceListResponseV2Api {
  readonly pagination: PaginationApi;
  readonly data: MappedSystemPackageVersionResourceReadApi[];
}

export interface GenericArtifactForUpload extends GenericArtifact {
  readonly templateTargetLocation?: string;
}

export declare const systemPackageComponentType: {
  readonly APPLICATION: 'APPLICATION';
  readonly OS: 'OS';
  readonly OTHER: 'OTHER';
  readonly SYSTEM_PACKAGE: 'SYSTEM_PACKAGE';
};
